import type { FC } from 'react';
import { useState, useContext, PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import BatchEditOrDeleteConfirmation from '../../../modals/BatchEditOrDeleteConfirmation';
import { BatchActionProps } from '../types';

import s from './MobileBatchActionWrapper.scss';

export interface Props extends BatchActionProps {
  selectedCount: number;
  onCancel: () => void;
  onSave: () => void;
  coreUpgradeBiData?: ReturnType<typeof BiData<BuyCoreButtonBiEvent>>;
  // actions can be restricted to Core users only, or non Core who can edit one or multiple items depending on each case
  restrictSave?: 'core' | 'non_core_single' | 'non_core_multiple';
}

const MobileBatchActionWrapper: FC<PropsWithChildren<Props>> = ({
  heading,
  children,
  selectedCount,
  disableSave,
  onCancel,
  onSave,
  coreUpgradeBiData,
  restrictSave = 'non_core_single',
}) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const [isShowingConfirmation, setShowingConfirmation] = useState(false);
  const canSave =
    hasCoreMembership ||
    restrictSave === 'non_core_multiple' ||
    (restrictSave === 'non_core_single' && selectedCount < 2);

  const biData =
    coreUpgradeBiData ||
    BiData<BuyCoreButtonBiEvent>({
      evid: 375,
      referral_info: ReferralType.STUDIO_BATCH_ACTION,
    });

  return (
    <div className={c(s['root'], 'ignore-react-onclickoutside')}>
      {heading && <div className={s['heading']}>{heading}</div>}
      {children}
      <div className={s['footer']}>
        <TextButton variant="main" size="medium" onClick={onCancel}>
          {t('studio.batchActions.cta.cancel')}
        </TextButton>
        {canSave ? (
          <BrandedButton
            variant="brand-green"
            size="medium"
            onClick={() => setShowingConfirmation(true)}
            disabled={disableSave}
          >
            {t('studio.batchActions.cta.save')}
          </BrandedButton>
        ) : (
          <BuyCoreBrandedButton
            size="medium"
            biData={biData}
            withSaleIndicator={false}
            withMobileDefaultLabel={false}
          >
            {t('studio.batchActions.cta.saveNonCore')}
          </BuyCoreBrandedButton>
        )}
      </div>

      {canSave && (
        <BatchEditOrDeleteConfirmation
          isOpen={isShowingConfirmation}
          selectedCount={selectedCount}
          onConfirm={onSave}
          onCancel={() => setShowingConfirmation(false)}
        />
      )}
    </div>
  );
};
MobileBatchActionWrapper.displayName = 'MobileBatchActionWrapper';

export default MobileBatchActionWrapper;
